import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { IconButton } from 'react-native-paper';

import { CloseIcon } from '@/assets';
import { MediaItemImage } from '@/features/media/components/MediaItemImage';
import type { MediaItemSize } from '@/features/media/types/mediaItem';
import { determineBestMediaItemSize } from '@/features/media/utils/mediaItem';
import { useFocusRing } from '@/shared/hooks/useFocusRing';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export const THUMB_SIZE = 96;

// Pick the rendition that best fits the default (thumbnail) size for this
// device's pixel density (THUMBNAIL on <=2x, EXTRA_SMALL on 3x) instead of
// always SMALL.
const THUMB_MEDIA_SIZE = determineBestMediaItemSize(THUMB_SIZE);

const DEFAULT_SIZE_STYLE: ViewStyle = { width: THUMB_SIZE, height: THUMB_SIZE };

type Props = {
  mediaItem?: ClientMediaItem;
  localImageUri?: string;
  isLoading?: boolean;
  /**
   * Rendition to request for the remote image. Defaults to a rendition picked
   * from the device pixel density for the default 96px size; pass a larger
   * size (e.g. `MediaItemSize.MEDIUM`) when rendering at preview size.
   */
  mediaSize?: MediaItemSize;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  hasError?: boolean;
  /** When provided, the remove button is rendered in the corner. */
  onRemove?: () => void;
  removeLabel?: string;
  removeDisabled?: boolean;
  /**
   * Optional drag-handle node rendered in the bottom-right corner (e.g. a
   * `Sortable.Handle` wrapping a drag icon). Kept as a slot so this component
   * stays free of the sorting library.
   */
  dragHandle?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

/**
 * A MediaItemImage at a caller-controlled size (default 96px), with rounded
 * corners and an optional remove button. Form-free and picker-free; the
 * form-specific variant is FormImageThumbnail (organization-edit).
 */
export const MediaImageSized: FC<Props> = ({
  mediaItem,
  localImageUri,
  isLoading = false,
  mediaSize = THUMB_MEDIA_SIZE,
  accessibilityLabel,
  accessibilityHint,
  hasError = false,
  onRemove,
  removeLabel,
  removeDisabled,
  dragHandle,
  style = DEFAULT_SIZE_STYLE
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  return (
    <View
      style={[
        styles.thumbWrapper,
        // With a remove button the wrapper must stay unclipped so the button
        // can stick out of the corner. Without one we clip so the image
        // corners stay rounded during swipe transforms (e.g. in carousels).
        !onRemove && styles.clip,
        style
      ]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint ?? accessibilityLabel}
    >
      <MediaItemImage
        mediaItem={mediaItem}
        localImageUri={localImageUri}
        mediaSize={mediaSize}
        isLoading={isLoading}
        style={[styles.thumb, hasError && styles.thumbError]}
      />
      {onRemove && (
        <RemoveImageButton
          label={removeLabel}
          onPress={onRemove}
          disabled={removeDisabled}
          style={styles.removeButton}
        />
      )}
      {dragHandle && <View style={styles.dragHandle}>{dragHandle}</View>}
    </View>
  );
};

type RemoveImageButtonProps = {
  label?: string;
  onPress: () => void;
  disabled?: boolean;
  style?: StyleProp<ViewStyle>;
};

const RemoveImageButton: FC<RemoveImageButtonProps> = ({
  label,
  onPress,
  disabled,
  style
}) => {
  const theme = useAppTheme();
  const focusRing = useFocusRing();

  return (
    <IconButton
      icon={({ size }) => (
        <CloseIcon
          fill={theme.colors.inverseOnSurface}
          width={size}
          height={size}
        />
      )}
      size={16}
      mode="contained"
      // Dark scrim-style button: the button sits on top of arbitrary images,
      // so a light surface color would wash out on bright photos.
      containerColor={theme.colors.inverseSurface}
      style={[style, focusRing.ringStyle]}
      accessibilityLabel={label}
      accessibilityHint={label}
      onPress={onPress}
      disabled={disabled}
      {...focusRing.focusProps}
    />
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    // No intrinsic size: the caller's `style` (or the THUMB_SIZE default
    // applied at the call site) dictates the dimensions. The wrapper and the
    // image both fill it so the corner remove button tracks the image edge.
    thumbWrapper: {
      width: '100%',
      height: '100%',
      borderRadius: 8,
      // Clipping is conditional: see the `clip` style and its call-site
      // comment. Default stays visible so the remove button can overflow.
      overflow: 'visible'
    },
    clip: {
      overflow: 'hidden'
    },
    thumb: {
      width: '100%',
      height: '100%',
      borderRadius: 8
    },
    thumbError: {
      borderWidth: 2,
      borderColor: theme.colors.error
    },
    removeButton: {
      position: 'absolute',
      top: -6,
      right: -6,
      margin: 0
    },
    dragHandle: {
      position: 'absolute',
      bottom: 2,
      right: 2,
      margin: 0
    }
  });
